import React, { useState } from 'react';
import { PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Multiline editable field component for notes and other longer text content
 */
const MultilineEditableField = ({ value, fieldName, shortName, placeholder, onChangeSuccess = null }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [fieldValue, setFieldValue] = useState(value || '');
  const [prevValue, setPrevValue] = useState(value || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle field click to enter edit mode
  const handleEditClick = () => {
    setPrevValue(fieldValue); // Store current value in case we need to revert
    setIsEditing(true);
  };
  
  // Handle field changes
  const handleChange = (e) => {
    setFieldValue(e.target.value);
  };
  
  // Handle saving the field value
  const handleSave = async () => {
    // If value hasn't changed, just exit edit mode
    if (fieldValue === prevValue) {
      setIsEditing(false);
      return;
    }
    
    setIsUpdating(true);
    setError(null);
    
    try {
      // Import is inside to avoid circular dependencies
      const { updateScopeField } = await import('../../utils/api');
      
      // Call the API to update the field
      const result = await updateScopeField(shortName, fieldName, fieldValue);
      
      if (result.success) {
        setIsEditing(false);
        
        // Call the success callback if provided, which will handle refreshing scope data
        if (onChangeSuccess && typeof onChangeSuccess === 'function') {
          onChangeSuccess();
        }
      } else {
        setError('Failed to update notes');
        // Don't revert to previous value here, let the user try again or cancel
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      setError(`Error: ${error.message}`);
      // Don't revert to previous value here, let the user try again or cancel
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Handle canceling the edit
  const handleCancel = () => {
    setFieldValue(prevValue); // Revert to previous value
    setIsEditing(false);
    setError(null);
  };
  
  if (isEditing) {
    return (
      <div className="relative">
        <textarea
          value={fieldValue}
          onChange={handleChange}
          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 min-h-[120px]"
          placeholder={placeholder}
          disabled={isUpdating}
          autoFocus
          rows={5}
        />
        
        <div className="mt-2 flex justify-end space-x-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isUpdating}
            className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-semibold shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <XMarkIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
            Discard
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            disabled={isUpdating}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
            Save
          </button>
        </div>
        
        {isUpdating && (
          <div className="absolute right-2 top-4">
            <span className="text-xs text-gray-500 animate-pulse">Saving...</span>
          </div>
        )}
        
        {error && (
          <p className="mt-1 text-xs text-red-500">{error}</p>
        )}
      </div>
    );
  }
  
  // Display view mode
  return (
    <div className="group">
      <div 
        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2"
        onClick={handleEditClick}
      >
        <div className="flex-1 text-gray-700">
          {fieldValue ? (
            <div className="whitespace-pre-wrap">{fieldValue}</div>
          ) : (
            <span className="text-gray-400">{placeholder || 'N/A'}</span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation(); // Prevent double triggering edit mode
            handleEditClick();
          }}
          className="ml-2 rounded-full p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          title={`Edit ${fieldName}`}
        >
          <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default MultilineEditableField;